import Loading from '../components/Loading';
import '../scss/view/prescription.scss';
import { useEffect, useState } from 'react';
import instance from '../config/axios';
import { useAuth } from 'react-oidc-context';
import { ColumnData, OrderTypeProps } from '../interface/interface';
import { Table } from '../components/Table';
import { PaginationState, RowSelectionState } from '@tanstack/react-table';
import qs from 'qs';
import Select, {
  DropdownIndicatorProps,
  MultiValue,
  components,
} from 'react-select';
import { onboardedOrInvitedList } from '../constant/constant';
import { DownCaretIcon, UserHeartIcon } from '../assets/svgImages';
import moment from 'moment';
import { onboardExistingTokens } from '../service';
import showToast from '../components/ToastComponent';
import ReportsDatePicker from '../components/OrderDatePicker';
import CustomModal from '../components/CustomModal';

interface Patient {
  id: number;
  token: string;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    dateOfBirth: string;
    email: string;
    active: boolean;
    isRegistered: boolean;
    isInvited: boolean;
    invitedAt: Date | null;
  };
}

function getStatus(isInvited: boolean, isOnboarded: boolean) {
  switch (true) {
    case !isOnboarded && isInvited:
      return { text: 'Not Onboarded', className: 'bg-primary-red-600' };

    case isOnboarded:
      return { text: 'Onboarded', className: 'bg-secondary-green-500' };

    case !isInvited:
      return { text: 'Not Invited', className: 'bg-primary-red-600' };

    case isInvited:
      return { text: 'Invited', className: 'bg-secondary-green-500' };
  }
}

const OnboardPatient = () => {
  const { user } = useAuth();

  const [patients, setPatients] = useState<Patient[] | []>([]);
  const [isLoading, setLoadingStatus] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isSearchSubmitted, setSearchSubmissionStaus] = useState<number>(0);
  const [filter, setFilter] = useState<
    MultiValue<{
      label: string;
      value: string;
    }>
  >([{ label: 'All Active', value: 'active' }]);
  const [invitationDateFilter, setInvitationDateFilter] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: moment(moment(localStorage.getItem('onboardFromDate'))).isValid()
      ? moment(localStorage.getItem('onboardFromDate')).toDate()
      : null,
    to: moment(localStorage.getItem('onboardToDate')).isValid()
      ? moment(localStorage.getItem('onboardToDate')).toDate()
      : null,
  });
  const [filterApplied, setFilterApplyStatus] = useState<boolean>(false);
  const [openModal, setModal] = useState<boolean>(false);
  const [paginationCount, setTotalPaginationCount] = useState<number>(0);
  const [submitCount, setSubmitCount] = useState<number>(0);

  const columns: Array<ColumnData> = [
    {
      key: 'select',
      header: () => 'Select',
      cell: ({ row, getValue }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected() || rowSelection?.[getValue()]}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    },
    {
      key: 'firstName',
      header: () => 'First Name',
      isSortable: true,
      cell: (info) => info.getValue(),
    },
    {
      key: 'lastName',
      header: () => 'Last Name',
      isSortable: true,
      cell: (info) => info.getValue(),
    },
    {
      key: 'dateOfBirth',
      header: () => 'Date of Birth',
      isSortable: true,
      cell: (info) => {
        const date = moment(new Date(info.getValue()));
        return date.isValid() ? moment(date).format('DD/MM/YYYY') : '-';
      },
    },
    {
      key: 'email',
      header: () => 'Email',
      isSortable: true,
      cell: (info) => info.getValue(),
    },
    {
      key: 'status',
      header: () => 'Status',
      cell: (info) => {
        const { isInvited, isRegistered } = info.row.original;
        const status = getStatus(isInvited, isRegistered);

        return (
          <span
            className={`px-5 py-2 rounded-full uppercase text-xs text-white inline-block text-center w-36
          font-semibold ${status?.className}`}
          >
            {status?.text}{' '}
          </span>
        );
      },
    },
    {
      key: 'invitedAt',
      header: () => 'Invited At',
      cell: (info) => (
        <p className="ml-6">
          {info.getValue()
            ? moment(info.getValue()).format('DD/MM/YYYY hh:mm a')
            : null}
        </p>
      ),
    },
  ];

  useEffect(() => {
    setLoadingStatus(true);
    const stringified = qs.stringify({
      page: pagination.pageIndex,
      limit: pagination.pageSize,
      search,
      filter: filter?.map((x) => x.value),
      invitedFromDate: invitationDateFilter?.from as Date,
      invitedToDate: invitationDateFilter?.to as Date,
    });

    instance({
      method: 'GET',
      url: `/api/v1/patients?${stringified}`,
      headers: {
        Authorization: `Bearer ${user?.access_token}`,
      },
    })
      .then((resp) => {
        setPatients(() => [
          ...resp.data.data.map((d: any) => ({
            ...d,
            select: d?.patientId,
            status: d?.isActive,
            isRegistered: d?.isRegistered,
            isInvited: !!d?.invitedAt,
            invitedAt: d?.invitedAt,
            edit: { patientId: d?.patientId },
          })),
        ]);
        setTotalRecords(resp?.data?.total);
        setLoadingStatus(false);

        if (
          paginationCount !==
            Math.ceil(resp.data.total / pagination.pageSize) &&
          submitCount < isSearchSubmitted
        ) {
          setTotalPaginationCount(
            Math.ceil(resp.data.total / pagination.pageSize),
          );
          setSubmitCount(isSearchSubmitted);
          setPagination({
            pageIndex: 0,
            pageSize: pagination.pageSize,
          });
        }
      })
      .catch((e: unknown) => {
        console.error('D> error fetching patients: ', e);
        setLoadingStatus(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, isSearchSubmitted, filter, filterApplied]);

  const DropdownIndicator = (props: DropdownIndicatorProps<true>) => {
    return (
      <components.DropdownIndicator {...props}>
        <DownCaretIcon />
      </components.DropdownIndicator>
    );
  };

  async function batchOnboard() {
    if (!user?.access_token) return;

    // Getting the keys
    const keys = Object.keys(rowSelection ?? {});
    const selectedPatients: number[] = [];

    for (const k of keys) {
      if (rowSelection?.[k]) selectedPatients.push(Number(k));
    }

    // Trigger onboard
    if (!selectedPatients.length) return;

    const response = await onboardExistingTokens(
      user.access_token,
      selectedPatients,
    );
    if (response === 201)
      showToast({ type: 'success', message: 'Patient onbarding  intialized!' });
    else showToast({ type: 'info', message: 'Unable to onboard patients' });

    setModal(false);
  }

  return (
    <>
      {(user?.profile.groups as string[]).includes('Admin') ||
      (user?.profile.groups as string[]).includes('PLO') ? (
        <>
          <div className="absolute top-0 right-0 mt-8">
            <button
              type="button"
              disabled={!Object.keys(rowSelection ?? {})?.length}
              onClick={() => setModal(true)}
              className={`ml-2 block rounded-md bg-secondary-pink-600 px-10 py-3 text-center text-sm font-semibold text-white flex items-center 
    ${!Object.keys?.(rowSelection ?? {})?.length ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <UserHeartIcon />
              <span className="ml-3">Onboard Selected</span>
            </button>
          </div>
        </>
      ) : null}

      <div className="mb-5 flex items-center">
        <Select
          className="w-48 cursor-pointer mr-4"
          value={filter as any}
          options={onboardedOrInvitedList as any}
          onChange={(selected) => setFilter([selected] as any)}
          isSearchable={false}
          components={{
            DropdownIndicator,
            IndicatorSeparator: () => null,
          }}
        />
        <ReportsDatePicker
          onExport={(from, to) => {
            setInvitationDateFilter({ to, from });
            setFilterApplyStatus((prev) => !prev);
          }}
          onClear={() => {
            setInvitationDateFilter({ from: null, to: null });
            setFilterApplyStatus((prev) => !prev);
            localStorage.removeItem('onboardFromDate');
            localStorage.removeItem('onboardToDate');
          }}
          btnText="Filter"
          from={invitationDateFilter.from ?? undefined}
          to={invitationDateFilter.to ?? undefined}
          keyStores={{
            from: 'onboardFromDate',
            to: 'onboardToDate',
          }}
        />
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <Table
          columns={columns}
          data={patients}
          enablePagination={true}
          enableRowSelection
          rowSelection={{
            state: rowSelection ?? {},
            setter: setRowSelection,
          }}
          enableSorting={true}
          enableSearch={true}
          pagintation={{
            state: pagination,
            setter: setPagination,
            totalRecords,
          }}
          search={{
            value: search,
            changeHandler: setSearch,
            submitSearch: setSearchSubmissionStaus,
          }}
          order={OrderTypeProps.ONBOARD}
        />
      )}

      <CustomModal
        isOpen={openModal}
        onClose={() => setModal(false)}
        header={'Confirmation'}
        footer={
          <>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
              onClick={() => setModal(false)}
            >
              Cancel
            </button>
            <button
              className="ml-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
              onClick={() => batchOnboard()}
            >
              Yes
            </button>
          </>
        }
        modalSize={'w-3/12'}
      >
        <p>Are you sure want to onboard the selected Patient(s) ?</p>
      </CustomModal>
    </>
  );
};

export default OnboardPatient;
